// @spec SUP-STR-007
'use client';

import { useOrganization } from '@clerk/clerk-react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import { Skeleton } from '@/components/ui/skeleton';
import {
  CategoryList,
  CreateCategoryDialog,
  DeleteCategoryDialog,
  EditCategoryDialog,
} from '@/features/settings/suppliers';
import type { SupplierCategory } from '@/features/settings/suppliers';
import { useOrgRole } from '@/hooks/use-org-role';
import i18n from '@/lib/i18n';

export const Route = createFileRoute('/_authenticated/settings/supplier-categories')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.supplierCategories'),
  }),
  component: SupplierCategoriesPage,
});

/**
 * Supplier categories management page.
 *
 * Shows list of supplier categories and allows admins
 * to create, edit, and delete categories.
 */
function SupplierCategoriesPage() {
  const navigate = useNavigate();
  const { organization } = useOrganization();
  const { isAdmin, isLoading: isLoadingRole } = useOrgRole();

  const [createOpen, setCreateOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<SupplierCategory | null>(null);
  const [deletingCategory, setDeletingCategory] = useState<SupplierCategory | null>(null);

  useEffect(() => {
    if (!isLoadingRole && !isAdmin) {
      void navigate({ to: '/dashboard' });
    }
  }, [isLoadingRole, isAdmin, navigate]);

  if (!isLoadingRole && !isAdmin) {
    return null;
  }

  const orgId = (organization?.publicMetadata as { internalId?: string } | undefined)?.internalId;

  if (isLoadingRole || !orgId) {
    return (
      <div className="p-8">
        <div className="mx-auto max-w-4xl space-y-6">
          <header>
            <Skeleton className="h-9 w-56" />
            <Skeleton className="h-5 w-96 mt-2" />
          </header>
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mx-auto max-w-4xl space-y-6">
        <header>
          <h1 className="text-3xl font-semibold text-foreground">Supplier Categories</h1>
          <p className="mt-1 text-muted-foreground">
            Manage supplier categories for your organization
          </p>
        </header>

        <CategoryList
          onCreateClick={() => {
            setCreateOpen(true);
          }}
          onEditClick={(category) => {
            setEditingCategory(category);
          }}
          onDeleteClick={(category) => {
            setDeletingCategory(category);
          }}
          isAdmin={isAdmin}
        />

        {/* Create Dialog */}
        <CreateCategoryDialog
          open={createOpen}
          onOpenChange={(open) => {
            setCreateOpen(open);
          }}
        />

        {/* Edit Dialog */}
        <EditCategoryDialog
          category={editingCategory}
          open={editingCategory !== null}
          onOpenChange={(open) => {
            if (!open) {
              setEditingCategory(null);
            }
          }}
        />

        {/* Delete Dialog */}
        <DeleteCategoryDialog
          category={deletingCategory}
          open={deletingCategory !== null}
          onOpenChange={(open) => {
            if (!open) {
              setDeletingCategory(null);
            }
          }}
        />
      </div>
    </div>
  );
}
